"use client"

/**
 * Debounced draft autosave driver for admin forms (Lane D).
 *
 * - Fires `onSave` after `debounceMs` of idle time, at most once every
 *   `minIntervalMs` (prevents storms when editor types fast).
 * - Tracks state: "idle" → "saving" → "saved" | "error".
 * - Disabled by default for safety. Callers opt in per form.
 * - Gated on `enabled` so published-record forms can turn it off.
 * - Signals dirty state to `window.__adminHasUnsavedWork` so the
 *   header site switcher's unsaved-work confirm (Lane C) fires.
 * - Never fires while `onSave` is in-flight — latest values win.
 */

import { useCallback, useEffect, useRef, useState } from "react"

export type AutosaveState = "idle" | "dirty" | "saving" | "saved" | "error"

interface UseFormAutosaveOptions {
  /** Master switch — disable for published entities. */
  enabled: boolean
  /** The value to save. Reference changes trigger autosave scheduling. */

  /** Persister. Must return a promise that resolves on success or throws. */
  onSave: () => Promise<void>
  /** Idle time before triggering a save (default 3s). */
  debounceMs?: number
  /** Minimum gap between saves (default 10s). */
  minIntervalMs?: number
  isDirty: boolean
}

export function useFormAutosave({
  enabled,
  onSave,
  isDirty,
  debounceMs = 3000,
  minIntervalMs = 10_000,
}: UseFormAutosaveOptions) {
  const [state, setState] = useState<AutosaveState>("idle")
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlightRef = useRef(false)
  const onSaveRef = useRef(onSave)

  useEffect(() => {
    onSaveRef.current = onSave
  }, [onSave])

  // Public "save now" — usable from shortcuts or the action bar.
  const saveNow = useCallback(async () => {
    if (inFlightRef.current) return
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    inFlightRef.current = true
    setState("saving")
    try {
      await onSaveRef.current()
      setState("saved")
      setLastSavedAt(new Date())
      if (typeof window !== "undefined") {
        ;(window as any).__adminHasUnsavedWork = false
      }
    } catch (err) {
      console.error("Autosave failed:", err)
      setState("error")
    } finally {
      inFlightRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    // First effect pass is initial mount with the form's default values.
    // Do NOT schedule a save — the form isn't dirty yet.
    if (!isDirty) {

      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      setState(prev => (lastSavedAt ? "saved" : "idle"))
      return
    }

    // Values changed. Mark dirty and schedule.
    setState(prev => (prev === "saving" ? prev : "dirty"))

    if (typeof window !== "undefined") {
      ;(window as any).__adminHasUnsavedWork = true
    }

    if (timerRef.current) clearTimeout(timerRef.current)
    const elapsedSinceLast = lastSavedAt ? Date.now() - lastSavedAt.getTime() : Infinity
    const delay = Math.max(debounceMs, minIntervalMs - elapsedSinceLast)

    timerRef.current = setTimeout(() => {
      timerRef.current = null
      saveNow()
    }, delay)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isDirty, enabled, debounceMs, minIntervalMs, lastSavedAt, saveNow])

  // Final flush on unmount — try to save pending changes before leaving.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        // Fire-and-forget; component is unmounting.
        if (enabled && !inFlightRef.current) {
          onSaveRef.current().catch(() => {
            /* swallowed — unmount path */
          })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { state, lastSavedAt, saveNow }
}
